import * as _ from 'lodash';
import React, { Component, Fragment } from 'react';
import { Dispatch, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { IApplicationState, AppActionCreators, IAppState, ReportActionCreators, ICenterDetailsResponse, ContactActionCreators, IContactLookupDataResponse } from '@nkr/store/index';
import { ICenterDetails, IContactEntityTypePositions } from '@nkr/models/index';
import { ContactEntityType, OrganType } from '@nkr/enums/index';
import { Button, Col, FormControl, Row } from 'react-bootstrap';
import DetailsHeader from './header-cards'
import ContactsGrid from './contacts-grid'
import AddressGrid from './address-grid'
import Serology from './serology';
import LabTubes from './lab-tubes';

interface IOwnProps extends RouteComponentProps<{ detailId: string }> {
    centerId: number
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCenterDetails: (id: number) => Promise<ICenterDetailsResponse>,
    getLookupData: (centerId: number, organType: number) => Promise<IContactLookupDataResponse>,
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    centerData: ICenterDetails,
    entityTypePositions: IContactEntityTypePositions[],
    searchName: string,
    role: number
}

const initialData: ICenterDetails = {
    name: '',
    exchangeCoordinator: {
        fullName: '',
        email: '',
        address: '',
        phone: '',
        isLead: false
    },
    kidneyPickup: null,
    kidneyDropOff: null,
    labTubeData: {
        id: 0,
        name: '',
        transYellow85: '',
        transRed70: '',
        transRed100: '',
        transLavender85: '',
        transLavender40: '',
        transLavender70: '',
        transLavender100: '',
        transPink60: '',
        transPink100: '',
        transGreen100: '',
        labYellow85: '',
        labRed70: '',
        labRed100: '',
        labLavender85: '',
        labLavender40: '',
        labLavender70: '',
        labLavender100: '',
        labPink60: '',
        labPink100: '',
        labGreen100: '',
        requiresQfg: true
    },
    contactList: [],
    addressList: [],
    serologyTests: []
}

class CenterSpecific extends Component<TProps, IState> {
    readonly state: IState = {
        centerData: initialData,
        entityTypePositions: [],
        searchName: '',
        role: 0
    }

    componentDidMount = () => {
        let { detailId } = this.props.match.params

        let centerId = parseInt(detailId);

        this.props.showLoading('Getting Center Data, please wait').then(() => {
            Promise.all([
                this.props.getCenterDetails(centerId),
                this.props.getLookupData(this.props.centerId, this.props.app.site.organType)
            ]).then(([centerDetails, lookupData]) => {
                if (centerDetails.data.success && lookupData.data.success) {
                    this.setState({
                        centerData: centerDetails.data.results,
                        entityTypePositions: lookupData.data.results.positions
                    }, () => this.props.hideLoading())
                }
            }).catch(() => {
                this.props.hideLoading();
            })
        }).catch(() => {
            this.props.hideLoading();
        })
    }

    getRoles = () => {
        let role = this.state.entityTypePositions.find(x => x.entityTypeId == ContactEntityType.Center);

        let positions = role && role.positions.filter(x => {
            if (this.props.app.site.organType == OrganType.Liver) {
                return x.description.indexOf('Liver') >= 0;
            }

            return true;
        });

        return positions;
    }

    getFilteredData = () => {
        let roles = this.getRoles();

        let d = this.state.centerData.contactList.filter(item => {
            let role = this.state.role > 0 ? item.positions.filter(x => x.id == this.state.role).length > 0 : true;
            let search = this.state.searchName != '' ? item.title.toUpperCase().indexOf(this.state.searchName.toUpperCase()) >= 0 : true;

            if (this.state.role == 0 && this.props.app.site.organType == OrganType.Liver) {
                role = item.positions.find(x => roles.filter(r => r.id == x.id).length > 0) != null;
            }

            return search && role;
        });

        return d;
    }

    getGroupList = () => {
        let groupList = this.state.entityTypePositions.filter(x => x.entityTypeId == ContactEntityType.Center);

        if (this.props.app.site.organType == OrganType.Liver) {
            groupList = groupList.filter(x => x.organType != OrganType.Kidney);
        }

        return groupList.map(group => {
            group.positions = _.sortBy(group.positions, item => item.description);
            return group;
        });
    }

    handleSearchChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        this.setState({
            searchName: e.currentTarget.value
        })
    }

    handleRoleChange = (e: React.FormEvent<HTMLSelectElement & FormControl>) => {
        this.setState({
            role: parseInt(e.currentTarget.value)
        })
    }

    handleReset = () => {
        this.setState({
            role: 0,
            searchName: ''
        })
    }

    render = () => {
        const { name, addressList, serologyTests, labTubeData, exchangeCoordinator, kidneyDropOff, kidneyPickup } = this.state.centerData;

        const filteredPositions = this.getFilteredData();
        const groupList = this.getGroupList();

        return (
            <div className='container-fluid'>
                <Row className='align-items-center'>
                    <Col xs={12} lg={12}>
                        <h4 className='mb-3'>{`${name} Contacts`}</h4>
                    </Col>
                </Row>

                <Row className='align-items-center'>
                    {exchangeCoordinator !== null &&
                        <Col xs={12} sm={4}>
                            <DetailsHeader
                                title={exchangeCoordinator.isLead ? 'Lead Exchange Coordinator' : 'Exchange Coordinator'}
                                name={exchangeCoordinator.fullName}
                                address={exchangeCoordinator.address}
                                email={exchangeCoordinator.email}
                                mobile={exchangeCoordinator.phone}
                                star={exchangeCoordinator.isLead}
                            />
                        </Col>
                    }

                    {kidneyPickup !== null &&
                        <Col xs={12} sm={4}>
                            <DetailsHeader
                                title={this.props.app.site.organType == OrganType.Kidney ? 'Kidney Pickup' : 'Liver Pickup'}
                                name={this.state.centerData.name}
                                address={kidneyPickup.fullAddress}
                            />
                        </Col>
                    }

                    {kidneyDropOff !== null &&
                        <Col xs={12} sm={4}>
                            <DetailsHeader
                                title={this.props.app.site.organType == OrganType.Kidney ? 'Kidney Drop-off' : 'Liver Drop-Off'}
                                name={this.state.centerData.name}
                                address={kidneyDropOff.fullAddress}
                            />
                        </Col>
                    }
                </Row>

                <Row className='align-items-center' style={{ marginBottom: '5px' }}>
                    <Col xs={12} sm={12}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                <div>Search:&nbsp;</div>
                                <FormControl
                                    style={{ width: 125 }}
                                    placeholder='Search by name...'
                                    value={this.state.searchName}
                                    onChange={this.handleSearchChange}
                                />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                                <div>Role:&nbsp;</div>
                                <div>
                                    <FormControl
                                        componentClass='select'
                                        value={this.state.role}
                                        onChange={this.handleRoleChange}
                                    >
                                        <option value={0}>- Any -</option>

                                        {groupList.length > 1 && groupList.map((group, index) =>
                                            <Fragment key={'role-group-' + index}>
                                                <optgroup label={group.entityType}>
                                                    {group.positions.map((position, positionIndex) =>
                                                        <option key={'role-group-item-' + positionIndex} value={position.id}>{position.description}</option>
                                                    )}
                                                </optgroup>
                                            </Fragment>
                                        )}

                                        {groupList.length == 1 && groupList.map((group, index) =>
                                            <Fragment key={'role-group-' + index}>
                                                {group.positions.map((position, positionIndex) =>
                                                    <option key={'role-group-item-' + positionIndex} value={position.id}>{position.description}</option>
                                                )}
                                            </Fragment>
                                        )}
                                    </FormControl>
                                </div>
                            </div>

                            <Button bsStyle='danger' style={{ marginRight: 15 }} onClick={this.handleReset}>Reset Filters</Button>
                        </div>
                    </Col>
                </Row>

                <Row className='align-items-center'>
                    <Col xs={12} sm={12}>
                        <ContactsGrid centerData={filteredPositions} />
                    </Col>
                </Row>

                <Row className='align-items-center'>
                    <Col xs={12} lg={12}>
                        <h4 className='mb-3'>{`${name} Addresses`}</h4>
                    </Col>
                </Row>

                <Row className='align-items-center'>
                    <Col xs={12} sm={6}>
                        <AddressGrid addressData={addressList} name={name} />
                    </Col>
                </Row>

                {this.props.app.site.organType == OrganType.Kidney && (
                    <Row className='align-items-center'>
                        <Col xs={12} sm={6}>
                            <Serology serologyTests={serologyTests} requiresQfg={labTubeData.requiresQfg} />
                        </Col>

                        {labTubeData !== null &&
                            <>
                                <Col xs={12} sm={3}>
                                    <LabTubes
                                        labTubeData={labTubeData}
                                        title={'Lab Tubes for Transport W/Donor Kidney'}
                                        label={'trans'}
                                    />
                                </Col>

                                <Col xs={12} sm={3}>
                                    <LabTubes
                                        labTubeData={labTubeData}
                                        title={'Lab Tubes for Cross Match'}
                                        label={'lab'}
                                    />
                                </Col>
                            </>
                        }
                    </Row>
                )}
            </div>
        )
    }
}

const mapStateToProps = (appState: IApplicationState, ownProps: IOwnProps): IStateProps => {
    return {
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getCenterDetails: (id: number) => {
            return dispatch(ReportActionCreators.getCenterDetails(id));
        },
        getLookupData: (centerId, organType) => {
            return dispatch(ContactActionCreators.getContactLookupData(centerId, organType));
        },
    };
}

export default withRouter(connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterSpecific));